// „Резервацията следва сметката" — чиста логика без React (тестове:
// followBills.test.ts). Огледало на BE `sync_seated_with_bills`: сметката на
// настанена резервация е преместена в Маркиране на друга маса от схемата →
// резервацията е вече на новата маса. BE пренася масите при всяко четене;
// FE прилага същото правило върху данните на деня, за да не се разминават
// платното и списъкът, ако отговорът още носи старата маса.

import type { OpenTable, Reservation } from "../../../api/reservations";
import { tableNameKey, type FloorTable } from "./tableStatus";

/**
 * Масите на настанената резервация според сметката ѝ — точно като BE
 * `service_follow.follow_bill_table`, който гледа само ПЪРВАТА маса (на нея
 * „Настани" отваря сметката):
 * - сметката е на първата маса, не е настанена/отворена → без промяна;
 * - сметката е на друга маса ОТ резервацията → тя застава първа, а първата
 *   отпада (редът на останалите се пази);
 * - сметката е на маса от схемата извън резервацията → тя заменя първата на
 *   нейното място;
 * - иначе („За вкъщи", „1А" от „Раздели") → без промяна.
 */
export const followBill = (
  reservation: Reservation,
  billsById: Map<number, OpenTable>,
  tablesByKey: Map<string, FloorTable>,
): Reservation => {
  if (reservation.status !== "seated" || reservation.billId === null) return reservation;
  const bill = billsById.get(reservation.billId);
  if (!bill || reservation.tableIds.length === 0) return reservation;
  const billKey = tableNameKey(bill.tableName);
  if (!billKey || tableNameKey(reservation.tableNames[0] ?? "") === billKey) return reservation;

  const ownIndex = reservation.tableNames.findIndex(
    (name, index) => index > 0 && tableNameKey(name) === billKey,
  );
  let ownId: number;
  let ownName: string;
  if (ownIndex > 0) {
    ownId = reservation.tableIds[ownIndex];
    ownName = reservation.tableNames[ownIndex];
  } else {
    const newTable = tablesByKey.get(billKey);
    if (!newTable) return reservation;
    ownId = newTable.id;
    ownName = newTable.name;
  }

  const tableIds = [ownId];
  const tableNames = [ownName];
  reservation.tableIds.forEach((id, index) => {
    if (index === 0 || id === ownId) return;
    tableIds.push(id);
    tableNames.push(reservation.tableNames[index]);
  });
  return { ...reservation, tableIds, tableNames };
};

/** `followBill` за всички резервации на деня (същият масив, ако няма промяна). */
export const followBills = (
  reservations: Reservation[],
  openTables: OpenTable[],
  tables: FloorTable[],
): Reservation[] => {
  if (!reservations.some((reservation) => reservation.status === "seated")) return reservations;
  const billsById = new Map(openTables.map((bill) => [bill.billId, bill]));
  const tablesByKey = new Map<string, FloorTable>();
  for (const table of tables) {
    const key = tableNameKey(table.name);
    if (key && !tablesByKey.has(key)) tablesByKey.set(key, table);
  }
  let changed = false;
  const followed = reservations.map((reservation) => {
    const next = followBill(reservation, billsById, tablesByKey);
    if (next !== reservation) changed = true;
    return next;
  });
  return changed ? followed : reservations;
};
